import axios from "axios";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { router } from "expo-router";
import secureStorage from "./secureStorage";
import { handleApiResponse } from "./freeTierUtils";
import { API_URL } from "@/lib/config";

const customAxios = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

const getAccessToken = async () => {
  return await AsyncStorage.getItem("accessToken");
};
const getRefreshToken = async () => {
  return await secureStorage.getItemAsync("refreshToken");
};
// Add a request interceptor to include the token in headers  /get   put the token in the headers
customAxios.interceptors.request.use(
  async (config) => {
    const token = await getAccessToken();

    if (token) {
      config.headers["Authorization"] = `JWT ${token}`; // Attach token to the request
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Add a response interceptor to handle expired access tokens and free tier limits
customAxios.interceptors.response.use(
  (response) => {
    // Check for free tier responses in successful responses
    if (response.config.url) {
      handleApiResponse(response.config.url, response.data);
    }
    return response;
  }, // If the response is successful, just return it
  async (error) => {
    try {
      const originalRequest = error?.config;

      // Safe navigation check
      if (
        error?.response &&
        (error.response.status === 401 || error.response.status === 403) &&
        originalRequest &&
        !originalRequest._retry
      ) {
        originalRequest._retry = true; // Prevent infinite loop
        try {
          const newAccessToken = await refreshAccessToken();
          if (originalRequest.headers) {
            originalRequest.headers["Authorization"] = `JWT ${newAccessToken}`;
          }
          return customAxios(originalRequest);
        } catch (err) {
          // Skip auto-redirect if the caller handles navigation itself (e.g. index.tsx auth check)
          if (!originalRequest._skipAuthRedirect) {
            setTimeout(() => {
              router.replace("/(routes)/(auth)/login");
            }, 100);
          }
        }
      }

      // Check for free tier limit exceeded in error responses (403 responses)
      if (
        error?.response &&
        error.response.status === 403 &&
        error.response.data &&
        error.config?.url
      ) {
        try {
          handleApiResponse(error.config.url, error.response.data);
        } catch (apiHandleError) {
          // silently ignore
        }
      }

      return Promise.reject(error);
    } catch (interceptorError) {
      return Promise.reject(error || interceptorError);
    }
  },
);

const refreshAccessToken = async () => {
  try {
    // Send a request to the refresh endpoint with the cookies included
    const response = await axios.post(
      `${API_URL}/mobile/refresh`,
      {
        refreshToken: await getRefreshToken(),
      },
      {
        withCredentials: true, // Ensures cookies are sent with the request
      },
    );
    // Extract the new access token from the response
    const { accessToken } = response.data;
    // Store the new access token in AsyncStorage
    await AsyncStorage.setItem("accessToken", accessToken);
    return accessToken;
  } catch (err) {
    // Handle error, maybe redirect to login
    throw new Error("Unable to refresh access token");
  }
};

export default customAxios;
